import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Select,
  MenuItem,
  Divider,
  TextField,
  Grid,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  Skeleton,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import TelegramIcon from "@mui/icons-material/Telegram";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SaveIcon from "@mui/icons-material/Save";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RepeatIcon from "@mui/icons-material/Repeat";
import NotificationWarning from "../../utils/NotificationWarning";
import ThemedDatePicker from "../../utils/ThemedDatePicker";
import ThemedTimePicker from "../../utils/ThemedTimePicker";
import ThemedSelect from "../../utils/ThemedSelect";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

// Scoped theme: restyles every MUI component on this screen (and its dialogs)
// without touching the component tree. Nothing outside this screen is affected.
const summaryTheme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: "#084767", contrastText: "#FFFFFF" },
    secondary: { main: "#4A3FBF", contrastText: "#FFFFFF" },
    success: { main: "#2FA864", dark: "#1B6B41", contrastText: "#FFFFFF" },
    error: { main: "#C42B24" },
    text: { primary: INK, secondary: "#4A5A6E" },
    action: { active: MUTED },
    divider: "#EDF1F5",
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: FONT,
    h5: { fontSize: 27, fontWeight: 800, letterSpacing: "-0.028em" },
    h6: { fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em" },
    subtitle2: { fontSize: 14, fontWeight: 700, letterSpacing: "-0.005em", color: INK },
    body1: { fontSize: 14 },
    body2: { fontSize: 13.5 },
    caption: { fontSize: 12 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none" },
        outlined: { border: `1px solid ${LINE}`, borderRadius: 14, background: "#FFFFFF" },
        elevation0: { border: `1px solid ${LINE}` },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 9, fontFamily: FONT, fontWeight: 600, minHeight: 36, paddingInline: 14 },
        sizeSmall: { minHeight: 30, fontSize: 12.5, paddingInline: 10, borderRadius: 8 },
        contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
        outlined: { borderColor: FIELD_EDGE, background: "#FFFFFF", "&:hover": { background: "#F4F7FA", borderColor: "#B4C6DA" } },
        outlinedSuccess: { borderColor: "#C4E8D2", color: "#1B6B41", "&:hover": { background: "#E9F7EE", borderColor: "#C4E8D2" } },
        outlinedSecondary: { borderColor: "#D3D0F2", color: "#4A3FBF", "&:hover": { background: "#EFEDFB", borderColor: "#D3D0F2" } },
        text: { color: PRIMARY, "&:hover": { background: "#EAF2FB" } },
        colorInherit: { color: INK_2 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 9, "&:hover": { background: "#F4F7FA" } },
        colorError: { color: "#A6342A", "&:hover": { background: "#FBEDEB" } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          background: "#FFFFFF",
          fontFamily: FONT,
          fontSize: 14,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
          "& input[type=date], & input[type=time], & input[type=number]": { fontFamily: MONO, fontSize: 13 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontFamily: FONT, fontSize: 14, color: MUTED, "&.Mui-focused": { color: PRIMARY } } },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontFamily: FONT, fontSize: 12, marginInline: 0 } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: 13.5,
          borderRadius: 8,
          minHeight: 36,
          "&.Mui-selected, &.Mui-selected:hover": { background: "#EAF2FB", color: "#1B4F82", fontWeight: 700 },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        option: { fontFamily: FONT, fontSize: 13.5, borderRadius: 8, marginInline: 6 },
        tag: { margin: 2 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: 12,
          background: "#EAF2FB",
          border: "1px solid #C9DEF3",
          color: "#1B4F82",
        },
        sizeSmall: { height: 24 },
        outlinedPrimary: { background: "#EAF2FB", borderColor: "#8FC3E6", color: "#1B4F82" },
        deleteIcon: { color: "#1B4F82", opacity: 0.6, "&:hover": { color: "#1B4F82", opacity: 1 } },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 18, boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)" },
      },
    },
    MuiBackdrop: {
      styleOverrides: { root: { "&:not(.MuiBackdrop-invisible)": { background: "rgba(15,27,42,0.46)" } } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK } },
    },
    MuiDialogContent: {
      styleOverrides: { dividers: { borderColor: "#EDF1F5" } },
    },
    MuiToggleButtonGroup: {
      styleOverrides: { root: { background: "#F4F7FA", padding: 3, borderRadius: 10, gap: 3 } },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          border: "0 !important",
          borderRadius: "8px !important",
          fontFamily: FONT,
          fontSize: 13,
          fontWeight: 600,
          color: INK_2,
          textTransform: "none",
          "&.Mui-selected, &.Mui-selected:hover": { background: "#FFFFFF", color: INK, fontWeight: 700, boxShadow: "0 1px 2px rgba(15,27,42,0.12)" },
        },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: "#EDF1F5" } } },
    MuiTooltip: {
      styleOverrides: { tooltip: { background: INK, fontFamily: FONT, fontSize: 12, fontWeight: 500, borderRadius: 7 } },
    },
    MuiSkeleton: { styleOverrides: { root: { background: "#F1F4F8" } } },
    MuiCircularProgress: { defaultProps: { thickness: 4.5 } },
  },
});

const UTM_DEFAULT = {
  utmSource: [],
  utmCampaign: [],
  utmMedium: [],
  utmContent: [],
  utmTerm: [],
};

const DAYS_OF_WEEK = [
  { label: "Monday", value: "MONDAY" },
  { label: "Tuesday", value: "TUESDAY" },
  { label: "Wednesday", value: "WEDNESDAY" },
  { label: "Thursday", value: "THURSDAY" },
  { label: "Friday", value: "FRIDAY" },
  { label: "Saturday", value: "SATURDAY" },
  { label: "Sunday", value: "SUNDAY" },
];

const reconstructFilters = (filters = []) => {
  const map = {};
  for (const f of filters) {
    if (
      f.filterKey === "status" ||
      f.filterKey === "utmSource" ||
      f.filterKey === "utmCampaign" ||
      f.filterKey === "utmMedium" ||
      f.filterKey === "utmContent" ||
      f.filterKey === "utmTerm"
    ) {
      if (!map[f.filterKey]) map[f.filterKey] = [];
      map[f.filterKey].push(f.filterValue);
    } else {
      map[f.filterKey] = f.filterValue;
    }
  }
  return map;
};

const SaveAndScheduleDialog = ({
  open,
  onClose,
  onSave,
  saving,
  activeFilterCount,
  activeFilterSummary,
  title,
  isExistingReport,
  rName,
}) => {
  const [reportName, setReportName] = useState("");
  const [reportNameError, setReportNameError] = useState("");
  const [frequency, setFrequency] = useState("DAILY");
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [weekDay, setWeekDay] = useState("MONDAY");
  const [monthDate, setMonthDate] = useState(1);

  // Reset state whenever dialog opens
  useEffect(() => {
    if (open) {
      setReportName(rName || "");
      setReportNameError("");
      setFrequency("DAILY");
      setScheduleTime("09:00");
      setWeekDay("MONDAY");
      setMonthDate(1);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!isExistingReport && !reportName.trim()) {
      setReportNameError("Report name is required.");
      return;
    }
    onSave({
      reportName: reportName.trim(),
      frequency,
      scheduleTime,
      weekDay: frequency === "WEEKLY" ? weekDay : undefined,
      monthDate: frequency === "MONTHLY" ? monthDate : undefined,
    });
  };

  const frequencyLabel = {
    DAILY: "Every day",
    WEEKLY: `Every ${DAYS_OF_WEEK.find((d) => d.value === weekDay)?.label}`,
    MONTHLY: `Monthly on the ${monthDate}${
      monthDate === 1
        ? "st"
        : monthDate === 2
          ? "nd"
          : monthDate === 3
            ? "rd"
            : "th"
    }`,
  }[frequency];

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "18px" } }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <RepeatIcon color="primary" />
          {title}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5 }}>
        {/* Report Name */}
        <Typography variant="subtitle2" fontWeight={600} mb={0.75} sx={{ fontSize: 12, color: INK_2 }}>
          Report Name
        </Typography>
        <TextField
          autoFocus
          placeholder="e.g. Q1 Google Leads"
          fullWidth
          size="small"
          value={reportName}
          onChange={(e) => {
            setReportName(e.target.value);
            if (reportNameError) setReportNameError("");
          }}
          error={!!reportNameError}
          helperText={reportNameError}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { height: 40 } }}
        />

        {/* Frequency selector */}
        <Typography variant="subtitle2" fontWeight={600} mb={1} sx={{ fontSize: 12, color: INK_2 }}>
          Frequency
        </Typography>
        <ToggleButtonGroup
          value={frequency}
          exclusive
          onChange={(_, val) => val && setFrequency(val)}
          size="small"
          sx={{
            mb: 2.5,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <ToggleButton
            value="DAILY"
            sx={{
              px: { xs: 1, sm: 2.5 },
              flex: { xs: 1, sm: "unset" },
              textTransform: "none",
            }}
          >
            Daily
          </ToggleButton>

          <ToggleButton
            value="WEEKLY"
            sx={{
              px: { xs: 1, sm: 2.5 },
              flex: { xs: 1, sm: "unset" },
              textTransform: "none",
            }}
          >
            Weekly
          </ToggleButton>

          <ToggleButton
            value="MONTHLY"
            sx={{
              px: { xs: 1, sm: 2.5 },
              flex: { xs: 1, sm: "unset" },
              textTransform: "none",
            }}
          >
            Monthly
          </ToggleButton>
        </ToggleButtonGroup>

        {/* ── DAILY: just time ── */}
        {frequency === "DAILY" && (
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 32 }}
            >
              At
            </Typography>
            <ThemedTimePicker
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              step={300}
              width={150}
            />
          </Box>
        )}

        {/* ── WEEKLY: day + time ── */}
        {frequency === "WEEKLY" && (
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            mb={2}
            flexWrap="wrap"
          >
            <CalendarTodayIcon fontSize="small" color="action" />
            <ThemedSelect
              value={weekDay}
              onChange={(e) => setWeekDay(e.target.value)}
              placeholder="Day of week"
              options={DAYS_OF_WEEK}
              height={40}
              fullWidth={false}
              sx={{ minWidth: 150 }}
            />
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 16 }}
            >
              at
            </Typography>
            <ThemedTimePicker
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              step={300}
              width={150}
            />
          </Box>
        )}

        {/* ── MONTHLY: date number + time ── */}
        {frequency === "MONTHLY" && (
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            mb={2}
            flexWrap="wrap"
          >
            <CalendarTodayIcon fontSize="small" color="action" />
            <TextField
              label="Date of month"
              type="number"
              size="small"
              value={monthDate}
              onChange={(e) => {
                const v = Math.min(28, Math.max(1, Number(e.target.value)));
                setMonthDate(v);
              }}
              inputProps={{ min: 1, max: 28 }}
              sx={{ width: 140 }}
              helperText="1 – 28"
            />
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 16 }}
            >
              at
            </Typography>
            <ThemedTimePicker
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              step={300}
              width={150}
            />
          </Box>
        )}

        {/* Live schedule preview */}
        <Box
          mt={1}
          px={2}
          py={1.25}
          sx={{
            background: "#E9F7EE",
            borderRadius: "10px",
            border: "1px solid #C4E8D2",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <RepeatIcon fontSize="small" sx={{ color: "#2FA864" }} />
          <Typography variant="caption" color="success.dark" fontWeight={600} sx={{ fontSize: 12.5 }}>
            Will run: {frequencyLabel} at <span style={{ fontFamily: MONO }}>{scheduleTime}</span>
          </Typography>
        </Box>

        {/* Active filters preview */}
        {activeFilterCount > 0 && (
          <Box
            mt={2}
            p={1.5}
            sx={{
              background: "#F7F9FC",
              borderRadius: "10px",
              border: `1px solid ${LINE}`,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
              fontWeight={700}
              sx={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: MUTED }}
            >
              Filters to be saved:
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.8, fontSize: 12.5 }}
            >
              {activeFilterSummary}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={
            saving ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <RepeatIcon />
            )
          }
        >
          {saving ? "Saving..." : "Save & Schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LeadStatusSummary = ({ projectId, project }) => {
  const user = JSON.parse(localStorage.getItem("user"));

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const showModal = (msg, type = "error") => {
    setModalMsg(msg);
    setModalType(type);
    setModalOpen(true);
  };

  // Confirm popup (replaces window.confirm). onYes runs when "Delete" is clicked.
  const [confirmMsg, setConfirmMsg] = useState("");
  const confirmAction = useRef(null);
  const askConfirm = (msg, onYes) => {
    confirmAction.current = onYes;
    setConfirmMsg(msg);
  };
  const closeConfirm = () => {
    confirmAction.current = null;
    setConfirmMsg("");
  };
  const runConfirm = () => {
    const fn = confirmAction.current;
    closeConfirm();
    if (fn) fn();
  };

  const [statuses, setStatuses] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [timeline, setTimeline] = useState("TODAY");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTo, setUpdatedTo] = useState("");
  const [lastCommentFrom, setLastCommentFrom] = useState("");
  const [lastCommentTo, setLastCommentTo] = useState("");
  const [scoreRange, setScoreRange] = useState([0, 100]);
  const [summary, setSummary] = useState({});
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [utmSources, setUtmSources] = useState([]);
  const [utmMediums, setUtmMediums] = useState([]);
  const [utmCampaigns, setUtmCampaigns] = useState([]);
  const [utmContents, setUtmContents] = useState([]);
  const [utmTerms, setUtmTerms] = useState([]);
  const [utmFilter, setUtmFilter] = useState(UTM_DEFAULT);
  const [scheduledTime, setScheduledTime] = useState(
    project?.scheduledSummaryTime?.slice(0, 5) || "",
  );
  const [savingTime, setSavingTime] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deletingTime, setDeletingTime] = useState(false);

  // Save Report Dialog
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [reportName, setReportName] = useState("");
  const [savingReport, setSavingReport] = useState(false);
  const [reportSaved, setReportSaved] = useState(false);
  const [reportNameError, setReportNameError] = useState("");

  // ── NEW: Save & Schedule Dialog ──
  const [saveScheduleDialogOpen, setSaveScheduleDialogOpen] = useState(false);
  const [savingScheduledReport, setSavingScheduledReport] = useState(false);

  // Saved Reports List
  const [savedReports, setSavedReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [activeReportId, setActiveReportId] = useState(null);
  const [loadingReportId, setLoadingReportId] = useState(null);
  const [deletingReportId, setDeletingReportId] = useState(null);
  const [updatingReport, setUpdatingReport] = useState(false);
  const isMobile = useMediaQuery("(max-width: 750px)");
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  const fetchSavedReports = async () => {
    try {
      setLoadingReports(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/report/all?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setSavedReports(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchStatuses = async () => {
    try {
      setLoadingStatuses(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/lead-status/all?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setStatuses(res.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingStatuses(false);
    }
  };

  const fetchUtm = async (path, setter) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/summary/${path}?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setter(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchSchedules = async (reportId) => {
    try {
      setLoadingSchedules(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/scheduler/by-report/${reportId}`,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      setSchedules(res.data || []);
    } catch (err) {
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchStatuses();
      fetchUtm("utm-sources", setUtmSources);
      fetchUtm("utm-mediums", setUtmMediums);
      fetchUtm("utm-contents", setUtmContents);
      fetchUtm("utm-terms", setUtmTerms);
      fetchUtm("utm-campaigns", setUtmCampaigns);
      fetchSavedReports();
    }
  }, [projectId]);

  const applyFiltersToState = (filterMap) => {
    setTimeline(filterMap.timeline || "");
    setCreatedFrom(filterMap.createdFrom || "");
    setCreatedTo(filterMap.createdTo || "");
    setUpdatedFrom(filterMap.updatedFrom || "");
    setUpdatedTo(filterMap.updatedTo || "");
    setLastCommentFrom(filterMap.lastCommentFrom || "");
    setLastCommentTo(filterMap.lastCommentTo || "");
    setScoreRange([
      filterMap.scoreMin ? Number(filterMap.scoreMin) : 0,
      filterMap.scoreMax ? Number(filterMap.scoreMax) : 100,
    ]);
    setSelectedStatuses(
      new Set(Array.isArray(filterMap.status) ? filterMap.status : []),
    );
    setUtmFilter({
      utmSource: filterMap.utmSource || [],
      utmCampaign: filterMap.utmCampaign || [],
      utmMedium: filterMap.utmMedium || [],
      utmContent: filterMap.utmContent || [],
      utmTerm: filterMap.utmTerm || [],
    });
  };

  const handleLoadReport = async (reportId) => {
    try {
      setLoadingReportId(reportId);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/report?reportId=${reportId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      applyFiltersToState(reconstructFilters(res.data.filters || []));
      setActiveReportId(reportId);
      fetchSchedules(reportId);
      setReportSaved(false);
      setReportName(res.data.reportName || "");
      setSummary({});
    } catch (err) {
      console.error(err);
      showModal("Failed to load report.");
    } finally {
      setLoadingReportId(null);
    }
  };

  const handleDeleteReport = (reportId) =>
    askConfirm("Delete this saved report?", () => deleteReport(reportId));

  const deleteReport = async (reportId) => {
    try {
      setDeletingReportId(reportId);
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/scheduler/by-report/${reportId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/report?reportId=${reportId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setSavedReports((prev) => prev.filter((r) => r.reportId !== reportId));
      if (activeReportId === reportId) {
        setActiveReportId(null);
        setReportSaved(false);
        setSchedules([]);
      }
    } catch (err) {
      console.error(err);
      showModal("Failed to delete report.");
    } finally {
      setDeletingReportId(null);
    }
  };

  const handleUpdateReport = async () => {
    if (!activeReportId) return;
    try {
      setUpdatingReport(true);
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/report`,
        { reportId: activeReportId, filterMap: buildFilterMap() },
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setSavedReports((prev) =>
        prev.map((r) => (r.reportId === activeReportId ? res.data : r)),
      );
      setReportSaved(true);
      setTimeout(() => setReportSaved(false), 3000);
    } catch (err) {
      console.error(err);
      showModal("Failed to update report.");
    } finally {
      setUpdatingReport(false);
    }
  };

  const toggleStatus = (status) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      next.has(status) ? next.delete(status) : next.add(status);
      return next;
    });
  };
  const selectAll = () =>
    setSelectedStatuses(new Set(statuses.map((s) => s.status)));
  const clearAll = () => setSelectedStatuses(new Set());
  const setUtmField = (field) => (val) =>
    setUtmFilter((prev) => ({ ...prev, [field]: val }));

  const resetAllFilters = () => {
    setTimeline("TODAY");
    setCreatedFrom("");
    setCreatedTo("");
    setUpdatedFrom("");
    setUpdatedTo("");
    setLastCommentFrom("");
    setLastCommentTo("");
    setScoreRange([0, 100]);
    setUtmFilter(UTM_DEFAULT);
    clearAll();
    setSummary({});
    setReportSaved(false);
    setActiveReportId(null);
    setSchedules([]);
  };

  const scheduleExists = (frequency, scheduleTime, weekDay, monthDate) => {
    return schedules.some((s) => {
      return (
        s.frequency === frequency &&
        s.scheduleTime === scheduleTime &&
        (s.weekDay || null) === (weekDay || null) &&
        String(s.monthDate || "") === String(monthDate || "")
      );
    });
  };

  const handleScheduleExistingReport = async ({
    reportName: rName,
    frequency,
    scheduleTime,
    weekDay,
    monthDate,
  }) => {
    try {
      if (scheduleExists(frequency, scheduleTime, weekDay, monthDate)) {
        showModal("This schedule already exists.");
        return;
      }

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/scheduler`,
        {
          projectId,
          reportId: activeReportId,
          frequency,
          scheduleTime,
          weekDay,
          monthDate,
          reportName: rName,
          filterMap: buildFilterMap(),
        },
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      fetchSchedules(activeReportId);

      showModal("Schedule created successfully", "success");
    } catch (err) {
      console.error(err);
      showModal("Failed to create schedule");
    } finally {
      setSaveScheduleDialogOpen(false);
    }
  };

  const buildFilterMap = () => {
    const f = {};
    if (timeline) f.timeline = timeline || "";
    if (createdFrom) f.createdFrom = createdFrom;
    if (createdTo) f.createdTo = createdTo;
    if (updatedFrom) f.updatedFrom = updatedFrom;
    if (updatedTo) f.updatedTo = updatedTo;
    if (lastCommentFrom) f.lastCommentFrom = lastCommentFrom;
    if (lastCommentTo) f.lastCommentTo = lastCommentTo;
    if (scoreRange[0] > 0) f.scoreMin = scoreRange[0];
    if (scoreRange[1] < 100) f.scoreMax = scoreRange[1];
    if (selectedStatuses.size > 0) f.status = Array.from(selectedStatuses);
    if (utmFilter.utmSource.length > 0) f.utmSource = utmFilter.utmSource;
    if (utmFilter.utmCampaign.length > 0) f.utmCampaign = utmFilter.utmCampaign;
    if (utmFilter.utmMedium.length > 0) f.utmMedium = utmFilter.utmMedium;
    if (utmFilter.utmContent.length > 0) f.utmContent = utmFilter.utmContent;
    if (utmFilter.utmTerm.length > 0) f.utmTerm = utmFilter.utmTerm;
    return f;
  };

  const buildParams = () => {
    const p = new URLSearchParams();
    p.set("projectId", projectId);
    const f = buildFilterMap();
    if (f.timeline) p.set("timeline", f.timeline);
    if (f.createdFrom) p.set("createdFrom", f.createdFrom);
    if (f.createdTo) p.set("createdTo", f.createdTo);
    if (f.updatedFrom) p.set("updatedFrom", f.updatedFrom);
    if (f.updatedTo) p.set("updatedTo", f.updatedTo);
    if (f.lastCommentFrom) p.set("lastCommentFrom", f.lastCommentFrom);
    if (f.lastCommentTo) p.set("lastCommentTo", f.lastCommentTo);
    if (f.scoreMin) p.set("scoreMin", f.scoreMin);
    if (f.scoreMax) p.set("scoreMax", f.scoreMax);
    if (f.status) p.set("status", f.status.join(","));
    if (f.utmSource) p.set("utmSource", f.utmSource.join(","));
    if (f.utmCampaign) p.set("utmCampaign", f.utmCampaign.join(","));
    if (f.utmMedium) p.set("utmMedium", f.utmMedium.join(","));
    if (f.utmContent) p.set("utmContent", f.utmContent.join(","));
    if (f.utmTerm) p.set("utmTerm", f.utmTerm.join(","));
    return p.toString();
  };

  const generateSummary = async () => {
    try {
      setLoadingSummary(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/summary?${buildParams()}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setSummary(res.data || {});
    } catch (err) {
      console.log(err);
      showModal("Failed to generate summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleOpenSaveDialog = () => {
    setReportName("");
    setReportNameError("");
    setSaveDialogOpen(true);
  };

  const handleSaveReport = async () => {
    if (!reportName.trim()) {
      setReportNameError("Report name is required.");
      return;
    }
    try {
      setSavingReport(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/report`,
        {
          projectId,
          reportName: reportName.trim(),
          filterMap: buildFilterMap(),
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      fetchSavedReports();
      setActiveReportId(res.data.reportId);
      setReportSaved(true);
      setSaveDialogOpen(false);
    } catch (err) {
      console.error(err);
      showModal("Failed to save report.");
    } finally {
      setSavingReport(false);
    }
  };

  const handleSaveAndSchedule = async ({
    reportName: rName,
    frequency,
    scheduleTime,
    weekDay,
    monthDate,
  }) => {
    try {
      setSavingScheduledReport(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/report`,
        {
          projectId,
          reportName: rName,
          filterMap: buildFilterMap(),
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      const newReportId = res.data.reportId;

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/scheduler`,
        {
          projectId,
          reportId: newReportId,
          frequency, // "DAILY" | "WEEKLY" | "MONTHLY"
          scheduleTime, // "HH:mm"
          weekDay, // e.g. "MONDAY" (only for WEEKLY)
          monthDate, // e.g. 15       (only for MONTHLY)
          reportName: rName,
          filterMap: buildFilterMap(),
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );

      fetchSavedReports();
      fetchSchedules(newReportId);
      setActiveReportId(newReportId);
      setReportSaved(true);
      setSaveScheduleDialogOpen(false);
    } catch (err) {
      console.error(err);
      showModal("Failed to save and schedule report.");
    } finally {
      setSavingScheduledReport(false);
    }
  };

  const sendToTelegram = async () => {
    if (!project.telegramBotId && !project.telegramChatId)
      return showModal("Telegram integration not configured for this project");
    try {
      const totalLeads = Object.values(summary).reduce((s, n) => s + n, 0);
      const message =
        totalLeads === 0
          ? timeline === "MISSED"
            ? "😊 Good Job!! No Missed Follow-Ups Yesterday 😊"
            : timeline === "TODAY"
              ? "😊 No Follow-Ups Scheduled For Today. 😊"
              : "😊 No Leads Found. 😊"
          : [
              "💥 Project Status Summary 💥",
              "",
              `Project ID: ${projectId}`,
              "",
              ...Object.entries(summary).map(([s, c]) => `${s}: ${c}`),
              "",
              `TOTAL LEADS: ${totalLeads}`,
            ].join("\n");
      await axios.post(
        `https://api.telegram.org/bot${project.telegramBotId}/sendMessage`,
        { chat_id: project.telegramChatId, text: message },
      );
      showModal("Summary sent successfully", "success");
    } catch (error) {
      console.error(error);
      showModal("Failed to send summary");
    }
  };

  const totalLeads = Object.values(summary).reduce((sum, n) => sum + n, 0);
  const getEmptyMessage = () => {
    if (timeline === "MISSED")
      return "Good job!! No missed follow-ups yesterday.";
    if (timeline === "TODAY") return "No follow-ups scheduled for today.";
    return "No leads found.";
  };

  const utmActive = Object.values(utmFilter).some((arr) => arr.length > 0);
  const activeFilterCount = [
    timeline,
    createdFrom || createdTo,
    updatedFrom || updatedTo,
    lastCommentFrom || lastCommentTo,
    scoreRange[0] > 0 || scoreRange[1] < 100,
    selectedStatuses.size > 0,
    utmActive,
  ].filter(Boolean).length;

  const activeFilterSummary = [
    timeline && `Timeline: ${timeline}`,
    (createdFrom || createdTo) &&
      `Created: ${createdFrom || "any"} → ${createdTo || "any"}`,
    (updatedFrom || updatedTo) &&
      `Updated: ${updatedFrom || "any"} → ${updatedTo || "any"}`,
    utmFilter.utmSource.length > 0 &&
      `Source: ${utmFilter.utmSource.join(", ")}`,
    utmFilter.utmCampaign.length > 0 &&
      `Campaign: ${utmFilter.utmCampaign.join(", ")}`,
    utmFilter.utmMedium.length > 0 &&
      `Medium: ${utmFilter.utmMedium.join(", ")}`,
    utmFilter.utmContent.length > 0 &&
      `Content: ${utmFilter.utmContent.join(", ")}`,
    utmFilter.utmTerm.length > 0 && `Term: ${utmFilter.utmTerm.join(", ")}`,
    (scoreRange[0] > 0 || scoreRange[1] < 100) &&
      `Score: ${scoreRange[0]}–${scoreRange[1]}`,
    selectedStatuses.size > 0 &&
      `Statuses: ${Array.from(selectedStatuses).join(", ")}`,
  ]
    .filter(Boolean)
    .join("  ·  ");

  const handleDeleteSchedule = (scheduleReportId) =>
    askConfirm("Delete this scheduled report?", () => deleteSchedule(scheduleReportId));

  const deleteSchedule = async (scheduleReportId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/scheduler/${scheduleReportId}`,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      fetchSchedules(activeReportId);
    } catch (err) {
      console.error(err);
      showModal("Failed to delete schedule");
    }
  };

  // Section label shared by every filter group below (visual only)
  const sectionLabelSx = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.11em",
    textTransform: "uppercase",
    color: MUTED,
  };

  return (
    <ThemeProvider theme={summaryTheme}>
    <Box py={2} sx={{ fontFamily: FONT, color: INK }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: isMobile ? "center" : "space-between",
          gap: 1,
        }}
        mb={2.5}
      >
        <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: 24, sm: 27 } }}>
          Lead Status Summary
        </Typography>
        {activeFilterCount > 0 && (
          <Button
            size="small"
            startIcon={<FilterListIcon />}
            onClick={resetAllFilters}
            color="inherit"
            variant="outlined"
          >
            Reset all filters ({activeFilterCount})
          </Button>
        )}
      </Box>
      <NotificationWarning message="Notifications are off. Enable them from your profile menu to receive report alerts." />
      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: "14px", mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1.75}>
          <BookmarkAddIcon fontSize="small" sx={{ color: PRIMARY }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 15 }}>
            Saved Reports
          </Typography>
          {savedReports.length > 0 && (
            <Chip
              label={savedReports.length}
              size="small"
              sx={{ ml: 0.5, fontFamily: MONO, background: "#F1F4F8", borderColor: LINE, color: MUTED }}
            />
          )}
        </Box>

        {loadingReports ? (
          <Box display="flex" flexDirection="column" gap={1}>
            {[1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: "10px" }} />
            ))}
          </Box>
        ) : savedReports.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No saved reports yet. Apply filters and click "Save report" to save
            one.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {savedReports?.map((report) => {
              const isActive = activeReportId === report.reportId;
              const isLoadingThis = loadingReportId === report.reportId;
              const isDeletingThis = deletingReportId === report.reportId;
              return (
                <Box
                  key={report.reportId}
                  gap={1}
                  px={1.75}
                  py={1.1}
                  sx={{
                    borderRadius: "10px",
                    border: "1px solid",
                    borderColor: isActive ? "#8FC3E6" : LINE,
                    background: isActive ? "#EAF2FB" : "#FFFFFF",
                    transition: "all .15s",
                    display: isMobile ? "block" : "flex",
                    alignItems: isMobile ? "flex-start" : "center",
                    "&:hover": { borderColor: isActive ? "#8FC3E6" : "#B4C6DA" },
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={isActive ? 700 : 500}
                    sx={{ flex: 1, color: INK }}
                  >
                    {report.reportName}
                  </Typography>

                  {isActive && (
                    <Chip
                      label="Active"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}

                  {/* Load */}
                  <Tooltip title="Load this report's filters">
                    <span>
                      <Button
                        size="small"
                        variant={isActive ? "contained" : "outlined"}
                        startIcon={
                          isLoadingThis ? (
                            <CircularProgress size={13} color="inherit" />
                          ) : (
                            <FolderOpenIcon fontSize="small" />
                          )
                        }
                        onClick={() => handleLoadReport(report.reportId)}
                        disabled={isLoadingThis || isDeletingThis}
                        sx={{ minWidth: 80 }}
                      >
                        {isLoadingThis ? "Loading..." : "Load"}
                      </Button>
                    </span>
                  </Tooltip>

                  {/* Delete */}
                  <Tooltip title="Delete this report">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteReport(report.reportId)}
                        disabled={isDeletingThis || isLoadingThis}
                      >
                        {isDeletingThis ? (
                          <CircularProgress size={16} color="error" />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
            {activeReportId && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  mb: 1,
                  mt: 0.5,
                  borderRadius: "12px",
                  background: "#F7F9FC",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} mb={1} sx={sectionLabelSx}>
                  Scheduled Reports
                </Typography>

                {loadingSchedules ? (
                  <CircularProgress size={20} />
                ) : schedules.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No schedules configured.
                  </Typography>
                ) : (
                  schedules.map((schedule) => (
                    <Box
                      key={schedule.scheduleReportId}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1}
                      sx={{ borderBottom: "1px solid #EDF1F5", "&:last-of-type": { borderBottom: 0 } }}
                    >
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <RepeatIcon sx={{ fontSize: 16, color: "#2FA864" }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                            {schedule.frequency}
                          </Typography>

                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>
                            {schedule.frequency === "WEEKLY"
                              ? `${schedule.weekDay} • ${schedule.scheduleTime}`
                              : schedule.frequency === "MONTHLY"
                                ? `${schedule.monthDate} • ${schedule.scheduleTime}`
                                : schedule.scheduleTime}
                          </Typography>
                        </Box>
                      </Box>

                      <IconButton
                        color="error"
                        size="small"
                        onClick={() =>
                          handleDeleteSchedule(schedule.scheduleReportId)
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))
                )}
              </Paper>
            )}
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.75 }, borderRadius: "14px" }}>
        {/* Timeline */}
        <Typography variant="subtitle2" mb={1} sx={sectionLabelSx}>
          Timeline{" "}
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            ml={1}
            sx={{ textTransform: "none", letterSpacing: 0, fontWeight: 500, fontSize: 12 }}
          >
            Filters by follow-up schedule date
          </Typography>
        </Typography>
        <ThemedSelect
          value={timeline}
          onChange={(e) => setTimeline(e.target.value)}
          placeholder="ALL"
          options={[
            { value: "", label: "ALL" },
            { value: "TODAY", label: "Today" },
            { value: "MISSED", label: "Missed" },
            { value: "YESTERDAY", label: "Yesterday" },
            { value: "LAST_7_DAYS", label: "Last 7 Days" },
            { value: "LAST_30_DAYS", label: "Last 30 Days" },
            { value: "THIS_MONTH", label: "This Month" },
            { value: "LAST_MONTH", label: "Last Month" },
          ]}
          height={40}
          fullWidth={false}
          sx={{ minWidth: 220, mb: 2.75 }}
        />

        <Divider sx={{ mb: 2.75 }} />

        {/* Date ranges */}
        <Typography variant="subtitle2" mb={1.75} sx={sectionLabelSx}>
          Date ranges
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
          gap={2}
          mb={2}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
              sx={{ fontWeight: 600, color: INK_2 }}
            >
              Created between
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: 1,
              }}
            >
              <ThemedDatePicker
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
                max={createdTo || undefined}
                height={40}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <Typography variant="caption" color="text.secondary">
                to
              </Typography>
              <ThemedDatePicker
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
                min={createdFrom || undefined}
                height={40}
                sx={{ flex: 1, minWidth: 0 }}
              />
            </Box>
          </Box>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
              sx={{ fontWeight: 600, color: INK_2 }}
            >
              Updated between
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: 1,
              }}
            >
              <ThemedDatePicker
                value={updatedFrom}
                onChange={(e) => setUpdatedFrom(e.target.value)}
                max={updatedTo || undefined}
                height={40}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <Typography variant="caption" color="text.secondary">
                to
              </Typography>
              <ThemedDatePicker
                value={updatedTo}
                onChange={(e) => setUpdatedTo(e.target.value)}
                min={updatedFrom || undefined}
                height={40}
                sx={{ flex: 1, minWidth: 0 }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
          gap={2}
          mb={2.75}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
              sx={{ fontWeight: 600, color: INK_2 }}
            >
              Last Comment between
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: 1,
              }}
            >
              <ThemedDatePicker
                value={lastCommentFrom}
                onChange={(e) => setLastCommentFrom(e.target.value)}
                max={lastCommentTo || undefined}
                height={40}
                sx={{ flex: 1, minWidth: 0 }}
              />
              <Typography variant="caption" color="text.secondary">
                to
              </Typography>
              <ThemedDatePicker
                value={lastCommentTo}
                onChange={(e) => setLastCommentTo(e.target.value)}
                min={lastCommentFrom || undefined}
                height={40}
                sx={{ flex: 1, minWidth: 0 }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 2.75 }} />

        {/* UTM */}
        <Typography variant="subtitle2" mb={1.75} sx={sectionLabelSx}>
          UTM Parameters
        </Typography>
        <Grid container spacing={1.5} mb={2.75}>
          {[
            { label: "UTM Source", field: "utmSource", options: utmSources },
            {
              label: "UTM Campaign",
              field: "utmCampaign",
              options: utmCampaigns,
            },
            { label: "UTM Medium", field: "utmMedium", options: utmMediums },
            { label: "UTM Content", field: "utmContent", options: utmContents },
            { label: "UTM Term", field: "utmTerm", options: utmTerms },
          ].map(({ label, field, options }) => (
            <Grid key={field} size={{ xs: 12, sm: 6, md: 2.4 }}>
              <MultiSelect
                label={label}
                options={options}
                value={utmFilter[field]}
                onChange={setUtmField(field)}
              />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 2.75 }} />

        {/* Score */}
        <Typography variant="subtitle2" mb={1.75} sx={sectionLabelSx}>
          Lead score{" "}
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            ml={1}
            sx={{ fontFamily: MONO, letterSpacing: 0, fontWeight: 600, fontSize: 12, color: PRIMARY }}
          >
            {scoreRange[0]} – {scoreRange[1]}
          </Typography>
        </Typography>
        <Box px={0} mb={2.75}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              gap: 1.5,
            }}
            mt={1}
          >
            <TextField
              label="Min"
              type="number"
              size="small"
              value={scoreRange[0]}
              onChange={(e) => {
                const val = Math.max(
                  0,
                  Math.min(Number(e.target.value), scoreRange[1] - 1),
                );
                setScoreRange([val, scoreRange[1]]);
              }}
              inputProps={{ min: 0, max: scoreRange[1] - 1 }}
              sx={{ width: isMobile ? "100%" : 90 }}
            />
            <TextField
              label="Max"
              type="number"
              size="small"
              value={scoreRange[1]}
              onChange={(e) => {
                const val = Math.min(
                  100,
                  Math.max(Number(e.target.value), scoreRange[0] + 1),
                );
                setScoreRange([scoreRange[0], val]);
              }}
              inputProps={{ min: scoreRange[0] + 1, max: 100 }}
              sx={{ width: isMobile ? "100%" : 90 }}
            />
            <Button
              size="small"
              onClick={() => setScoreRange([0, 100])}
              disabled={scoreRange[0] === 0 && scoreRange[1] === 100}
            >
              Reset
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2.75 }} />

        {/* Statuses */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: isMobile ? "flex-start" : "space-between",
            gap: 1,
          }}
          mb={1.5}
        >
          <Typography variant="subtitle2" sx={sectionLabelSx}>Statuses</Typography>
          <Box display="flex" gap={1}>
            <Button size="small" onClick={selectAll}>
              Select all
            </Button>
            <Button size="small" onClick={clearAll}>
              Clear
            </Button>
          </Box>
        </Box>
        {loadingStatuses ? (
          <CircularProgress size={24} />
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1} mb={2.75}>
            {statuses.map((status) => {
              const isSelected = selectedStatuses.has(status.status);
              return (
                <Box
                  key={status.statusId}
                  onClick={() => toggleStatus(status.status)}
                  sx={{
                    px: 1.5,
                    minHeight: 34,
                    borderRadius: "999px",
                    border: "1px solid",
                    borderColor: isSelected ? "#8FC3E6" : FIELD_EDGE,
                    background: isSelected ? "#EAF2FB" : "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    transition: "all .15s",
                    userSelect: "none",
                    "&:hover": { borderColor: isSelected ? "#8FC3E6" : "#B4C6DA", background: isSelected ? "#EAF2FB" : "#F7F9FC" },
                  }}
                >
                  {isSelected ? (
                    <CheckCircleIcon sx={{ fontSize: 17 }} color="primary" />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ fontSize: 17, color: "#C3CDD9" }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    fontWeight={isSelected ? 700 : 500}
                    sx={{ fontSize: 13, color: isSelected ? "#1B4F82" : INK_2 }}
                  >
                    {status.status}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Active filter summary */}
        {activeFilterCount > 0 && (
          <Paper
            variant="outlined"
            sx={{ p: 1.5, mb: 2, background: "#F7F9FC", borderRadius: "10px" }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12.5, lineHeight: 1.7 }}>
              <strong style={{ color: INK }}>Active filters:</strong> {activeFilterSummary}
            </Typography>
          </Paper>
        )}

        {/* Action buttons */}
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<AnalyticsIcon />}
            onClick={generateSummary}
            disabled={loadingSummary}
            sx={{ height: 40, px: 2.25 }}
          >
            {loadingSummary ? "Generating..." : "Generate summary"}
          </Button>
        </Box>

        {/* Results */}
        {!loadingSummary && Object.keys(summary).length > 0 && (
          <>
            <Divider sx={{ my: 2.75 }} />
            <Paper
              sx={{
                p: 0,
                background: "#FFFFFF",
                borderRadius: "14px",
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 2.25, pt: 2, pb: 1.5, borderBottom: "1px solid #EDF1F5" }}>
                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ mb: 0.25 }}>
                  💥 Project Status Summary 💥
                </Typography>
                <Typography variant="body2" mb={0} sx={{ fontFamily: MONO, fontSize: 12.5, color: MUTED }}>
                  Project ID: {projectId}
                </Typography>
              </Box>
              <Box sx={{ px: 2.25, py: 0.75 }}>
                {totalLeads === 0 ? (
                  <Typography
                    textAlign="center"
                    fontWeight={700}
                    sx={{ py: 3, color: "#1B6B41", fontSize: "1.05rem" }}
                  >
                    {getEmptyMessage()}
                  </Typography>
                ) : (
                  Object.entries(summary)
                    .sort((a, b) => b[1] - a[1])
                    .map(([status, count]) => (
                      <Box
                        key={status}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        py={0.9}
                        sx={{ borderBottom: "1px solid #F1F4F8", "&:last-of-type": { borderBottom: 0 } }}
                      >
                        <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: INK_2 }}>{status}</Typography>
                        <Typography fontWeight={700} sx={{ fontFamily: MONO, fontSize: 14 }}>{count}</Typography>
                      </Box>
                    ))
                )}
              </Box>
              <Divider sx={{ my: 0 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2.25, py: 1.5, background: "#F7F9FC" }}>
                <Typography fontWeight={800} sx={{ fontSize: 12, letterSpacing: "0.09em" }}>TOTAL LEADS</Typography>
                <Typography fontWeight={800} sx={{ fontFamily: MONO, fontSize: 18, color: PRIMARY }}>{totalLeads}</Typography>
              </Box>
              <Box sx={{ px: 2.25, pb: 2, pt: 0.25, display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                <Button
                  sx={{ mt: 2, mr: 1.25, background: "#229ED9", "&:hover": { background: "#1C8DC2" } }}
                  variant="contained"
                  startIcon={<TelegramIcon />}
                  onClick={sendToTelegram}
                >
                  Send to Telegram
                </Button>
                {!activeReportId && (
                  <Button
                    sx={{ mt: 2, mr: 1.25 }}
                    variant="outlined"
                    startIcon={<BookmarkAddIcon />}
                    onClick={handleOpenSaveDialog}
                    disabled={activeFilterCount === 0}
                  >
                    Save report
                  </Button>
                )}
                {schedules?.length === 0 && (
                  <Button
                    sx={{ mt: 2, mr: 1.25 }}
                    variant="outlined"
                    color="secondary"
                    startIcon={<RepeatIcon />}
                    onClick={() => setSaveScheduleDialogOpen(true)}
                    disabled={activeFilterCount === 0}
                  >
                    {activeReportId
                      ? "Schedule Report"
                      : "Save & Schedule Report"}
                  </Button>
                )}
                {savedReports?.map((report) =>
                  activeReportId === report.reportId ? (
                    <Tooltip
                      title="Save current filters to this report"
                      key={report.reportId}
                    >
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={
                            updatingReport ? (
                              <CircularProgress size={13} color="inherit" />
                            ) : reportSaved ? (
                              <CheckCircleOutlineIcon fontSize="small" />
                            ) : (
                              <SaveIcon fontSize="small" />
                            )
                          }
                          onClick={handleUpdateReport}
                          disabled={updatingReport}
                          sx={{ minWidth: 90, mt: 2, minHeight: 36 }}
                        >
                          {updatingReport
                            ? "Saving..."
                            : reportSaved
                              ? "Saved!"
                              : "Update Filters"}
                        </Button>
                      </span>
                    </Tooltip>
                  ) : null,
                )}
              </Box>
            </Paper>
          </>
        )}
      </Paper>

      <Dialog
        open={saveDialogOpen}
        onClose={() => !savingReport && setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Save Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Give this filter combination a name so you can re-run it anytime.
          </Typography>
          <TextField
            autoFocus
            label="Report name"
            placeholder="e.g. Q1 Google Leads"
            fullWidth
            size="small"
            value={reportName}
            onChange={(e) => {
              setReportName(e.target.value);
              if (reportNameError) setReportNameError("");
            }}
            error={!!reportNameError}
            helperText={reportNameError}
            onKeyDown={(e) => e.key === "Enter" && handleSaveReport()}
            sx={{ mt: 0.5 }}
          />
          {activeFilterCount > 0 && (
            <Box
              mt={2}
              p={1.5}
              sx={{
                background: "#F7F9FC",
                borderRadius: "10px",
                border: `1px solid ${LINE}`,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.5}
                fontWeight={700}
                sx={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: MUTED }}
              >
                Filters to be saved:
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.8, fontSize: 12.5 }}
              >
                {activeFilterSummary}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setSaveDialogOpen(false)}
            disabled={savingReport}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveReport}
            disabled={savingReport}
            startIcon={
              savingReport ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <BookmarkAddIcon />
              )
            }
          >
            {savingReport ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <SaveAndScheduleDialog
        open={saveScheduleDialogOpen}
        onClose={() => setSaveScheduleDialogOpen(false)}
        title={activeReportId ? "Schedule Report" : "Save & Schedule Report"}
        onSave={
          activeReportId ? handleScheduleExistingReport : handleSaveAndSchedule
        }
        saving={savingScheduledReport}
        activeFilterCount={activeFilterCount}
        activeFilterSummary={activeFilterSummary}
        isExistingReport={!!activeReportId}
        rName={reportName}
      />

      <ErrorModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        msg={modalMsg}
        type={modalType}
      />

      {/* Delete confirmation (replaces window.confirm) */}
      <Dialog
        open={!!confirmMsg}
        onClose={closeConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "18px", p: "24px 22px 20px", textAlign: "center", fontFamily: FONT },
        }}
        slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            margin: "0 auto 12px",
            borderRadius: "50%",
            background: "#FBEDEB",
            border: "1px solid #F2CFCA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 26, color: "#C42B24" }} />
        </div>
        <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK }}>
          Are you sure?
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 14, color: "#4A5A6E", mt: 0.75, mb: 2.5 }}>
          {confirmMsg}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Button
            fullWidth
            onClick={closeConfirm}
            sx={{
              height: 42,
              borderRadius: "10px",
              border: `1px solid ${FIELD_EDGE}`,
              background: "#FFFFFF",
              color: INK,
              fontFamily: FONT,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#F4F7FA" },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            onClick={runConfirm}
            sx={{
              height: 42,
              borderRadius: "10px",
              background: "#C42B24",
              color: "#FFFFFF",
              fontFamily: FONT,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#A6342A" },
            }}
          >
            Delete
          </Button>
        </Box>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
};

export default LeadStatusSummary;

const MultiSelect = ({ label, options, value, onChange }) => (
  <Autocomplete
    multiple
    options={options}
    value={value || []}
    onChange={(e, newValue) => onChange(newValue)}
    disableCloseOnSelect
    size="small"
    renderInput={(params) => <TextField {...params} label={label} />}
  />
);